// ============================================
// Dashboard Data Hooks
// ============================================

import useSWR from 'swr';
import type { DateRange, DashboardSummary, SpendTrendData, ChannelPerformance, PipelineData } from '../types/api';
import * as api from '../services/api';
import type { BusinessUnitData, DailyConversion } from '../services/api';

// SWR configuration
const SWR_CONFIG = {
  revalidateOnFocus: false,
  revalidateOnReconnect: true,
  dedupingInterval: 60000, // 1 minute
  errorRetryCount: 3,
};

/**
 * Hook for dashboard summary (KPIs)
 */
export function useDashboardSummary(dateRange: DateRange) {
  const key = dateRange ? ['dashboard-summary', dateRange.start, dateRange.end] : null;

  return useSWR<DashboardSummary>(
    key,
    () => api.getDashboardSummary(dateRange),
    {
      ...SWR_CONFIG,
      refreshInterval: 5 * 60 * 1000, // Refresh every 5 minutes
    }
  );
}

/**
 * Hook for spend trend data
 */
export function useSpendTrend(dateRange: DateRange) {
  const key = dateRange ? ['spend-trend', dateRange.start, dateRange.end] : null;

  return useSWR<SpendTrendData>(
    key,
    () => api.getSpendTrend(dateRange),
    SWR_CONFIG
  );
}

/**
 * Hook for channel performance
 */
export function useChannelPerformance(dateRange: DateRange) {
  const key = dateRange ? ['channel-performance', dateRange.start, dateRange.end] : null;

  return useSWR<{ channels: ChannelPerformance[] }>(
    key,
    () => api.getChannelPerformance(dateRange),
    SWR_CONFIG
  );
}

/**
 * Hook for conversion funnel
 */
export function useConversionFunnel(dateRange: DateRange) {
  const key = dateRange ? ['conversion-funnel', dateRange.start, dateRange.end] : null;

  return useSWR<{ stages: any[] }>(
    key,
    () => api.getConversionFunnel(dateRange),
    SWR_CONFIG
  );
}

/**
 * Hook for business unit data
 */
export function useBusinessUnits(dateRange: DateRange) {
  const key = dateRange ? ['business-units', dateRange.start, dateRange.end] : null;

  return useSWR<BusinessUnitData>(
    key,
    () => api.getBusinessUnits(dateRange),
    SWR_CONFIG
  );
}

/**
 * Hook for daily conversions (messages + leads)
 */
export function useDailyConversions(dateRange: DateRange) {
  const key = dateRange ? ['daily-conversions', dateRange.start, dateRange.end] : null;

  return useSWR<DailyConversion[]>(
    key,
    () => api.getDailyConversions(dateRange),
    SWR_CONFIG
  );
}

/**
 * Hook for top campaigns
 */
export function useTopCampaigns(dateRange: DateRange, limit = 10, sortBy = 'spend') {
  const key = dateRange ? ['top-campaigns', dateRange.start, dateRange.end, limit, sortBy] : null;

  return useSWR<{ campaigns: any[] }>(
    key,
    () => api.getTopCampaigns(dateRange, { limit, sortBy }),
    SWR_CONFIG
  );
}

/**
 * Hook for pipeline data
 */
export function usePipelineData(dateRange: DateRange) {
  const key = dateRange ? ['pipeline', dateRange.start, dateRange.end] : null;

  return useSWR<PipelineData>(
    key,
    () => api.getPipelineData(dateRange),
    SWR_CONFIG
  );
}

/**
 * Hook for ETL sync status
 */
export function useETLStatus() {
  return useSWR(
    'etl-status',
    () => api.getETLStatus(),
    {
      ...SWR_CONFIG,
      refreshInterval: 60 * 1000, // Refresh every minute
    }
  );
}

/**
 * Combined hook for all dashboard data
 */
export function useDashboardData(dateRange: DateRange) {
  const summary = useDashboardSummary(dateRange);
  const spendTrend = useSpendTrend(dateRange);
  const businessUnits = useBusinessUnits(dateRange);
  const dailyConversions = useDailyConversions(dateRange);
  const topCampaigns = useTopCampaigns(dateRange);

  const isLoading =
    summary.isLoading ||
    spendTrend.isLoading ||
    businessUnits.isLoading ||
    dailyConversions.isLoading ||
    topCampaigns.isLoading;

  const error =
    summary.error ||
    spendTrend.error ||
    businessUnits.error ||
    dailyConversions.error ||
    topCampaigns.error;

  return {
    isLoading,
    error,
    data: {
      summary: summary.data,
      spendTrend: spendTrend.data,
      businessUnits: businessUnits.data,
      dailyConversions: dailyConversions.data,
      topCampaigns: topCampaigns.data,
    },
    mutate: {
      summary: summary.mutate,
      spendTrend: spendTrend.mutate,
      businessUnits: businessUnits.mutate,
      dailyConversions: dailyConversions.mutate,
      topCampaigns: topCampaigns.mutate,
    },
  };
}
